import TableContainer from 'Components/Common/TableContainerReactTable';
import React from 'react';
import { Spinner, Button } from 'reactstrap';
// import PropertyInformationModal from '../Modals/PropertyInformationModal';
import { formatDateTime, formatPrice, formatStringWithUnderscores, getTruncatedId } from 'lib/utils/formatters';
// import PropertyInformationModal from '../Modals/PropertyInformationModal';

interface RentPropertiesListingProps {
    data: any[];
    loading: boolean;
    isError?: boolean;
    currentPage?: number;
    totalPages?: number;
    totalCount?: number;
    onPageChange?: (page: number) => void;
    onRefresh?: () => void;
    isRefetching?: boolean;
}

const RentPropertiesListing = ({
    data,
    loading,
    isError,
    currentPage = 1,
    totalPages = 1,
    totalCount = 0,
    onPageChange,
    onRefresh,
    isRefetching
}: RentPropertiesListingProps) => {
    // const [modalOpen, setModalOpen] = React.useState(false);
    // const [selectedPerson, setSelectedPerson] = React.useState(null);
    const [copiedId, setCopiedId] = React.useState<string | null>(null);


    const customPageSize = 10;

    const copyToClipboard = async (fullId: string) => {
        try {
            await navigator.clipboard.writeText(fullId);
            setCopiedId(fullId);
            // Reset the tick icon after 1 second
            setTimeout(() => {
                setCopiedId(null);
            }, 1000);
        } catch (err) {
            console.error('Failed to copy ID:', err);
        }
    };

    const columns = [
        {
            header: 'No',
            enableColumnFilter: false,
            cell: ({ row }: { row: any }) => {
                const index = (currentPage - 1) * customPageSize + row.index + 1;
                return <span>{index}</span>;
            },
        },
        {
            header: 'Property ID',
            accessorKey: 'propertyUnitId',
            enableColumnFilter: false,
            cell: ({ row }: { row: any }) => {
                const fullId = row.original?.propertyUnit?.id || 'N/A';
                if (!fullId) return 'N/A';

                return (
                    <div className="d-flex align-items-center gap-2">
                        <span style={{ fontSize: '14px', fontFamily: 'monospace' }}>
                            {getTruncatedId(fullId)}
                        </span>
                        <span
                            onClick={() => copyToClipboard(fullId)}
                            style={{
                                cursor: 'pointer',
                                fontSize: '14px',
                                color: copiedId === fullId ? '#28a745' : '#6b7280',
                                transition: 'color 0.2s ease'
                            }}
                            title={copiedId === fullId ? 'Copied!' : 'Copy full ID'}
                        >
                            {copiedId === fullId ? (
                                <i className="ri-check-line"></i>
                            ) : (
                                <i className="ri-file-copy-line"></i>
                            )}
                        </span>
                    </div>
                );
            },
        },
        {
            header: "Address",
            accessorKey: "property.property_address",
            enableColumnFilter: false,
            cell: ({ getValue }: { getValue: () => any }) => (
                <span>{getValue() || 'N/A'}</span>
            )
        },
        {
            header: "Listed For",
            accessorKey: "property.property_category",
            enableColumnFilter: false,
            cell: ({ getValue }: { getValue: () => any }) => (
                <span>{formatStringWithUnderscores(getValue()) || 'N/A'}</span>
            )
        },
        {
            header: "Builder",
            accessorKey: "builder.company_name",
            enableColumnFilter: false,
            cell: ({ getValue }: { getValue: () => any }) => (
                <span>{getValue() || 'N/A'}</span>
            )
        },
        {
            header: "Rent Amount",
            accessorKey: "amount",
            enableColumnFilter: false,
            cell: ({ getValue }: { getValue: () => any }) => (
                <span>
                    ${formatPrice(getValue() || 0)}
                </span>
            )
        },
        {
            header: "Days to Rent",
            accessorKey: "daysToRent",
            enableColumnFilter: false,
            cell: ({ getValue }: { getValue: () => any }) => (
                <span>
                    {getValue() || 0}
                </span>
            )
        },
        {
            header: "Agreement Date",
            accessorKey: "createdAt",
            enableColumnFilter: false,
            cell: ({ getValue }: { getValue: () => any }) => (
                <span>{formatDateTime(getValue()) || 'N/A'}</span>
            )
        },
        {
            header: 'Status',
            accessorKey: 'status', // yahan data me 'listingStatus' hai
            enableColumnFilter: false,
            cell: ({ row }: { row: any }) => {
                const status = row?.original?.status;
                // Show "Pending" when status is "created"
                const displayStatus = status === "created" ? "Pending" : formatStringWithUnderscores(status);
                switch (status) {
                    case "created":
                    case "Pending":
                        return (<span className="badge rounded-pill bg-warning"> {displayStatus}</span>);
                    case "signing_complete":
                    case "accepted":
                    case "public":
                    case "payment_pending":
                    case "earnest_paid":
                    case "sold":
                    case "rented":
                    case "signed_by_customer":
                        return (<span className="badge rounded-pill bg-success"> {displayStatus}</span>);
                    case "rejected":
                    case "archived":
                    case "expired":
                        return (<span className="badge rounded-pill bg-danger"> {displayStatus}</span>);
                    default:
                        return (<span className="badge rounded-pill  bg-warning"> {displayStatus}</span>);
                }
            },
        },
        // {
        //     header: "Action",
        //     accessorKey: "action",
        //     enableColumnFilter: false,
        //     cell: ({ row }: { row: any }) => (
        //         <span
        //             style={{ cursor: "pointer" }}
        //             onClick={() => {
        //                 setSelectedPerson(row.original);
        //                 setModalOpen(true);
        //             }}
        //         >
        //             <i className="ri-eye-line" style={{ fontSize: 20, color: "#6b7280" }}></i>
        //         </span>
        //     ),
        // },
    ];

    // Error state
    if (isError) {
        return (
            <div className="text-center py-5">
                <div className="text-danger mb-3">
                    <i className="ri-error-warning-line" style={{ fontSize: '3rem' }}></i>
                </div>
                <h5 className="text-danger">Failed to load properties</h5>
                <p className="text-muted">Please check your connection and try again.</p>
                <Button color="primary" onClick={onRefresh}>
                    <i className="ri-refresh-line me-1"></i>
                    Retry
                </Button>
            </div>
        );
    }

    return (
        <React.Fragment>
            {loading || isRefetching ? (
                <div className="text-center py-5">
                    <Spinner color="primary" />
                    <p className="mt-2 text-muted">Loading properties...</p>
                </div>
            ) : (
                <TableContainer
                    columns={columns}
                    data={data}
                    customPageSize={customPageSize}
                    tableClass="table-centered align-middle table-nowrap mb-0"
                    theadClass="text-muted table-light"
                    isServerSide={true}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalCount={totalCount}
                    onPageChange={onPageChange}
                />
            )}
            {/* <PropertyInformationModal
                isOpen={modalOpen}
                toggle={() => setModalOpen(false)}
                propertyId={selectedPerson}
            /> */}
        </React.Fragment>
    );
};

export default RentPropertiesListing;
